import React, { useEffect, useMemo, useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import ProjectCard from "@/components/ProjectCard";
import api from "@/lib/api";
import { useAgent } from "@/lib/agentContext";
import { track, withUtm } from "@/lib/track";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Browse({ mode = "buy" }) {
  const agent = useAgent();
  const [projects, setProjects] = useState([]);
  const [localities, setLocalities] = useState({ "Pune West": [], "Pune East": [] });
  const [search, setSearch] = useState("");
  const [zone, setZone] = useState(agent?.micro_market || "all");
  const [locality, setLocality] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/localities").then(({ data }) => setLocalities(data));
    const params = agent?.micro_market ? { zone: agent.micro_market } : {};
    api.get("/master-projects", { params }).then(({ data }) => { setProjects(data); setLoading(false); });
  }, [agent?.micro_market]);

  const filtered = useMemo(() => projects.filter((p) => {
    const m1 = zone === "all" || p.zone === zone;
    const m2 = locality === "all" || p.locality_tag === locality;
    const m3 = !search || p.project_name.toLowerCase().includes(search.toLowerCase()) ||
               p.developer.toLowerCase().includes(search.toLowerCase());
    return m1 && m2 && m3;
  }), [projects, zone, locality, search]);

  const isNewLaunch = mode === "new-launches";
  const heroOverline = agent
    ? (isNewLaunch ? `NEW LAUNCHES · ${agent.micro_market?.toUpperCase()}` : `BUY · ${agent.micro_market?.toUpperCase()} INVENTORY`)
    : (isNewLaunch ? "NEW LAUNCHES · PUNE" : "BUY · PUNE INVENTORY");
  const heroTitle = agent
    ? (isNewLaunch
        ? `Fresh launches across ${agent.micro_market}.`
        : `Browse ${agent.name.split(" ")[0]}'s ${agent.micro_market} inventory.`)
    : (isNewLaunch ? "Fresh launches across Pune's hottest corridors." : "Browse Pune's curated inventory.");
  const heroCopy = agent
    ? `Curated by ${agent.name} · MahaCRITI Reg No: ${agent.maharera_number}. Every tap routes straight to ${agent.name.split(" ")[0]}'s WhatsApp.`
    : (isNewLaunch
        ? "Sub-launch and just-launched towers from Pune's most active developers — across Baner, Balewadi, Kharadi, Wagholi and beyond."
        : "Every project is MahaCRITI-verified. Every WhatsApp tap routes to a real, accountable agent.");

  const waNum = agent?.phone ? agent.phone.replace(/[^0-9]/g, "") : null;
  const inquireHrefFor = (p) => {
    if (!agent || !waNum) return undefined;
    return withUtm(
      `https://wa.me/${waNum}?text=${encodeURIComponent(`Hi ${agent.name}, I'm interested in ${p.project_name} (${p.locality_tag}).`)}`,
      { agent_slug: agent.slug, project_id: p.project_id, source: "microsite" }
    );
  };
  const detailHrefFor = (p) => agent ? `/agent/${agent.slug}/project/${p.project_id}` : `/project/${p.project_id}`;
  const onInquireFor = (p) => agent ? () => track({ event_type: "wa_click", agent_slug: agent.slug, project_id: p.project_id, source: "microsite" }) : undefined;

  const inner = (
    <>
      <section className="bg-sg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="overline text-sg-gold mb-5">{heroOverline}</div>
          <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] max-w-3xl">{heroTitle}</h1>
          <p className="mt-6 text-white/75 max-w-2xl">{heroCopy}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 -mt-10">
        <div className="bg-white border border-sg-line rounded-3xl p-5 flex flex-col md:flex-row md:items-center gap-3 shadow-sm">
          <Input
            placeholder="Search by project or developer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="md:max-w-xs rounded-full border-sg-line"
            data-testid="browse-search"
          />
          {!agent && (
            <Select value={zone} onValueChange={(v) => { setZone(v); setLocality("all"); }}>
              <SelectTrigger className="md:w-44 rounded-full border-sg-line" data-testid="browse-zone">
                <SelectValue placeholder="All corridors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All corridors</SelectItem>
                <SelectItem value="Pune West">Pune West</SelectItem>
                <SelectItem value="Pune East">Pune East</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Select value={locality} onValueChange={setLocality}>
            <SelectTrigger className="md:w-56 rounded-full border-sg-line" data-testid="browse-locality">
              <SelectValue placeholder="All localities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All localities</SelectItem>
              {(zone === "all"
                ? [...localities["Pune West"], ...localities["Pune East"]]
                : (localities[zone] || [])
              ).map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="text-sm text-sg-mute md:ml-auto font-mono px-2" data-testid="browse-count">
            {filtered.length} of {projects.length} projects
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-sg-mute animate-pulse">Loading inventory…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sg-mute py-16 text-center border border-dashed border-sg-line rounded-3xl">
            No projects match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <ProjectCard
                key={p.project_id}
                project={p}
                tag={isNewLaunch ? "NEW LAUNCH" : "LIVE INVENTORY"}
                testIdPrefix="browse-project"
                inquireHref={inquireHrefFor(p)}
                detailHref={detailHrefFor(p)}
                onInquire={onInquireFor(p)}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );

  return agent ? inner : <PublicLayout>{inner}</PublicLayout>;
}
